import React, { useState, useEffect, Fragment, Suspense } from "react";
import styled, { css } from "styled-components";
import HardwareRequirements from "./HardwareRequirements";
import { QuestionsProps } from "./Questions.types";
import { useTranslation } from "hooks/useTranslation";
import { BREAKPOINTS } from "@ubnt/store.core/lib/constants/layout";
import { Tabs as TabsBase } from "components/Tabs/Tabs";
import LoadingSvg from "../assets/loading.svg";
import ArrowDownPrimarySvg from "../assets/arrow-down-primary.svg";

const Markdown = React.lazy(() =>
  import("./MarkdownWrap").then(({ MarkdownWrap }) => ({
    default: MarkdownWrap,
  })),
);

const Section = styled.div`
  position: relative;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.neutral00};

  padding-bottom: 80px;

  @media (max-width: ${({ theme }) => theme.Breakingpoint.md}px) {
    padding-bottom: 40px;
  }
`;

const Header = styled.div`
  ${({ theme }) => theme.HeaderDefault}
`;

const Title = styled.div`
  ${({ theme }) => theme.TitleDefault}
  color: ${({ theme }) => theme.colors.text1};
  font-size: 42px;
  font-weight: 700;
  line-height: 1.2;
`;

const Description = styled.div`
  ${({ theme }) => theme.DescriptionDefault}
  color: ${({ theme }) => theme.colors.text2};
  font-size: 16px;
  line-height: ${19 / 16};
  max-width: 760px;
  margin-left: auto;
  margin-right: auto;
`;

const Footer = styled.div`
  ${({ theme }) => theme.HeaderDefault}
  padding-top: 48px;

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    padding-top: 32px;
  }

  ${Description} {
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const Actions = styled.div`
  text-align: center;
`;

export const Action = styled.a`
  display: inline-block;
  height: 40px;
  line-height: 36px;
  ${({ theme }) => theme.Transition("all", 0.3)}
  border: 2px solid ${({ theme }) => theme.colors.ublue06};
  color: ${({ theme }) => theme.colors.neutral00};
  background-color: ${({ theme }) => theme.colors.ublue06};
  border-radius: 3px;
  margin-left: 5px;
  margin-right: 5px;
  font-weight: 700;
  font-size: 14px;
  padding-left: 20px;
  padding-right: 20px;
  &:hover,
  &:active,
  &:focus {
    border-color: ${({ theme }) => theme.colors.ublue06};
    color: ${({ theme }) => theme.colors.ublue06};
    background-color: transparent;
  }
`;

export const Body = styled.div`
  padding-top: 40px;
  max-width: 1195px;
  margin-left: auto;
  margin-right: auto;
`;

const Faq = styled.div``;

export const FaqQue = styled.a`
  display: block;
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.neutral11};
  padding-top: 18px;
  padding-bottom: 18px;
  position: relative;
  padding-right: 40px !important;
  padding-left: 20px;
  ${({ theme }) => theme.Transition("color", 0.3)}

  &:before {
    display: block;
    content: " ";
    position: absolute;
    right: 20px;
    top: 50%;
    margin-top: -7px;
    width: 14px;
    height: 14px;
    background-position: 50% 50%;
    background-image: url(${ArrowDownPrimarySvg});
    ${({ theme }) => theme.Transition("transform", 0.6)}
  }
  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    color: ${({ theme }) => theme.colors.black};
  }
`;
const FaqAns = styled.div`
  font-size: 16px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.neutral08};
  overflow: hidden;
  opacity: 0;
  height: 0px;
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 20px;
  padding-right: 20px;
  ${({ theme }) => theme.Transition("all", 0.3)}
  text-wrap: pretty;

  ul {
    padding-top: 0;
    padding-left: 20px;
    line-height: 26px;
    ul {
      padding-top: 0;
      line-height: 22px;
    }
  }

  a {
    color: ${({ theme }) => theme.colors.ublue06};
  }

  iframe {
    max-width: 100%;
    -webkit-mask-image: -webkit-radial-gradient(white, black);
    transform: translateZ(0);
    -webkit-backface-visibility: hidden;
    -webkit-transform: translateZ(0);
    border: 3px solid ${({ theme }) => theme.colors.neutral02};
    border-radius: 10px;
    position: relative;
    overflow: hidden;
    margin-left: auto;
    margin-right: auto;
    display: flex;
    margin-bottom: 10px;
  }
`;

const faq_ans_active = css`
  height: auto;
  padding-top: 15px;
  padding-bottom: 30px;
  opacity: 1;
`;

const faq_que_active = css`
  transform: rotate(180deg);
`;

const FaqItem = styled.div<{ $active: boolean }>`
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral04};
  ${FaqQue} {
    &:before {
      ${({ $active }) => $active && faq_que_active};
    }
  }
  ${FaqAns} {
    ${({ $active }) => $active && faq_ans_active};
  }
`;

const FaqsLoading = styled.div`
  text-align: center;
  padding-bottom: 40px;
  padding-top: 20px;
`;

const tabsClassName = "tab-custom-classname";

const Tabs = styled(TabsBase)`
  margin-top: 16px;

  .${tabsClassName} > button {
    padding: 8px 16px;

    & > span {
      font-weight: 700;
    }
  }
`;

const getObjectComponent = (faq) => {
  if (faq.answer.id === "hw") {
    return (
      <>
        {faq.answer.textBefore && (
          <Suspense fallback={null}>
            <Markdown>{faq.answer.textBefore}</Markdown>
          </Suspense>
        )}
        <HardwareRequirements {...faq.answer} />
        {faq.answer.textAfter && (
          <Suspense fallback={null}>
            <Markdown>{faq.answer.textAfter}</Markdown>
          </Suspense>
        )}
      </>
    );
  }

  // It doesn't look like this is used anywhere
  // if (faq.answer.id === "pricing") {
  //   return <Pricing {...faq.answer} />;
  // }
};

const renderMarkdownFaqs = (faqs, toggleFaq, activeFaqs) => {
  return faqs.map((faq) => {
    const isSpan = faq.answer.type === "span";
    const isObject = typeof faq.answer === "object";

    return (
      <FaqItem
        key={faq.id}
        $active={activeFaqs.includes(faq.id) ? true : false}
      >
        <FaqQue onClick={(e) => toggleFaq(e, faq.id)}>{faq.title}</FaqQue>
        <FaqAns>
          {activeFaqs.includes(faq.id) && (
            <Fragment>
              {typeof faq.answer === "string" && (
                <Suspense fallback={null}>
                  <Markdown>{faq.answer}</Markdown>
                </Suspense>
              )}
              {isObject && !isSpan && getObjectComponent(faq)}
              {isObject && isSpan && { ...faq.answer }}
            </Fragment>
          )}
        </FaqAns>
      </FaqItem>
    );
  });
};

const getStaticFaqs = async (faqs) => {
  return Object.keys(faqs).map((key) => faqs[key]);
};

const useFaqData = (faqs) => {
  const [faqData, setFaqData] = useState([]);
  useEffect(() => {
    getStaticFaqs(faqs)
      .then((faqs) => {
        return setFaqData(faqs);
      })
      .catch(() => {});
  }, [faqs]);

  return faqData;
};

const Questions = ({
  title,
  link,
  list,
  className,
  tabs,
  selectedTab,
  onTabSelect,
}: QuestionsProps) => {
  const { t } = useTranslation();
  const faqData = useFaqData(list);
  const [loadingFaqs, setLoadingFaqs] = useState(true);
  const [activeFaqs, setActiveFaqs] = useState([]);

  const toggleFaq = (e, id) => {
    e.preventDefault();
    if (!activeFaqs.includes(id)) {
      setActiveFaqs([...activeFaqs, id]);
    } else {
      const faqIndex = activeFaqs.indexOf(id);
      if (faqIndex !== -1) {
        const splice = activeFaqs.filter(function (prevId) {
          return prevId !== id;
        });
        setActiveFaqs(splice);
      }
    }
  };

  useEffect(() => {
    setLoadingFaqs(false);
    return () => {};
  }, [link, list]);

  useEffect(() => {
    if (selectedTab) {
      setActiveFaqs([]);
    }
  }, [selectedTab]);

  // #############################################################################
  // Render
  // #############################################################################
  return (
    <Section className={className} id="faq">
      <Header>
        <Title>{title ?? t("commons.faq")}</Title>
        {tabs?.length && selectedTab && onTabSelect && (
          <Tabs
            tabs={tabs}
            selected={selectedTab}
            onSelect={onTabSelect}
            tabsClassName={tabsClassName}
          />
        )}
      </Header>
      <Body className="container-fluid">
        {loadingFaqs && (
          <FaqsLoading>
            <img src={LoadingSvg} alt="Loading..." />
          </FaqsLoading>
        )}
        {faqData && (
          <Faq>{renderMarkdownFaqs(faqData, toggleFaq, activeFaqs)}</Faq>
        )}
      </Body>
      <Footer>
        <Actions>
          <Action href={link.url}>{link.title}</Action>
        </Actions>
      </Footer>
    </Section>
  );
};

export default Questions;
